'use client';

import React, { useEffect, useRef } from 'react';
import Script from 'next/script';
import { usePathname } from 'next/navigation';
import { clearCookies, META_COOKIES } from './trackerCookies';
import { useConsent } from './useConsent';

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void };
    _fbq?: unknown;
  }
}

/**
 * Loads the Meta pixel, and only once someone has agreed to it.
 *
 * The three calls are in Meta's documented order — init, then consent, then the
 * event. Consent came first here originally, which reads sensibly and is not
 * what fbevents.js expects: its own pattern is revoke before init and grant
 * after, so a grant arriving before there is a pixel to grant it for has
 * nothing to apply to.
 *
 * This one is different in kind from the other two and the difference is not
 * cosmetic. Analytics and Hotjar are processors: they hold data on our
 * instructions and do nothing else with it. Meta is not. It receives the visit
 * and uses it for its own advertising purposes, which under Fashion ID
 * (CJEU C-40/17) makes this site and Meta joint controllers for the collection
 * and transmission — and obtaining consent for that transmission is entirely
 * our job, not Meta's. Hence: no pixel, no request, no cookie until yes.
 *
 * The consequence for the visitor is stated in the banner rather than buried
 * here: this is the purpose that sends data to a company we do not control.
 */
export default function MetaPixel({ pixelId }: { pixelId: string }) {
  const { choice } = useConsent();
  const granted = choice?.marketing === true;
  const pathname = usePathname();

  useEffect(() => {
    if (!choice || choice.marketing) return;
    /* Meta's own consent gate, for a pixel already in this page, plus its
       first-party cookies. `fr` lives on facebook.com and is not ours. */
    window.fbq?.('consent', 'revoke');
    clearCookies(META_COOKIES);
  }, [choice]);

  /* A route change is a new page view to the pixel exactly as it is to
     analytics, and the snippet reports only the first one by itself.

     Which is the catch: the snippet has already reported that first one, so
     firing here on mount too counted every landing page twice. Meta has no way
     to tell a duplicate from a genuine second view, so it inflated PageView on
     every session and would have skewed the cost-per-result the campaigns are
     judged on. The ref starts true to skip exactly that one report — the same
     guard AnalyticsPageViews uses, for the same reason. */
  const reported = useRef(true);
  useEffect(() => {
    if (!granted) return;
    if (reported.current) {
      reported.current = false;
      return;
    }
    window.fbq?.('track', 'PageView');
  }, [pathname, granted]);

  if (!granted) return null;

  return (
    <Script id="meta-pixel" strategy="afterInteractive">
      {`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?` +
        `n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;` +
        `n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;` +
        `t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}` +
        `(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');` +
        `fbq('init','${pixelId}');fbq('consent','grant');fbq('track','PageView');`}
    </Script>
  );
}
